import { LEGAL_ENTITY, legalIdentityLines } from "@/config/legal";
import { A, ContactEmail, LegalDocument, LegalSection, LegalSubheading, LegalTable, List, P } from "./LegalLayout";

/**
 * Privacy Policy for The Polished Version itself (thepolishedversion.com).
 * Facts here were checked against the code and infrastructure on 2 November
 * 2026 — keep them in step when providers, storage or retention change.
 */

const TOC = [
  { id: "who-we-are", heading: "Who we are" },
  { id: "our-two-roles", heading: "Our two roles" },
  { id: "what-we-collect", heading: "Information we collect" },
  { id: "how-we-use-it", heading: "How we use it and our lawful basis" },
  { id: "who-we-share-with", heading: "Who we share it with" },
  { id: "international-transfers", heading: "International transfers" },
  { id: "how-long", heading: "How long we keep it" },
  { id: "security", heading: "How we keep it safe" },
  { id: "your-rights", heading: "Your rights" },
  { id: "complaints", heading: "Complaints" },
  { id: "children", heading: "Children" },
  { id: "changes", heading: "Changes to this policy" },
  { id: "contact", heading: "Contact us" },
];

export function PlatformPrivacy() {
  const identity = legalIdentityLines();
  return (
    <LegalDocument
      slug="privacy"
      title="Privacy Policy"
      intro="This policy explains how The Polished Version collects and uses personal information when you visit thepolishedversion.com, sign up, or use our booking website and admin tools to run your cleaning business."
      toc={TOC}
      summary={
        <List
          items={[
            "We are responsible (the “controller”) for information about business owners, their team members and visitors to our own website.",
            "When a cleaning business uses The Polished Version to take bookings, that business is responsible for its customers’ information. We store and process it on the business’s behalf (as its “processor”).",
            "We don’t sell personal information, and we don’t use advertising or analytics trackers.",
            "Our database is hosted in the EU (Ireland). Some of our service providers are based in the US, and we use approved legal safeguards for those transfers.",
            <>
              Business owners can download their data or delete their business at any time. Anyone can contact us at <ContactEmail />.
            </>,
          ]}
        />
      }
    >
      <LegalSection id="who-we-are" heading="Who we are">
        <P>
          The Polished Version provides booking websites and business admin tools for cleaning businesses in the UK. In this
          policy, “we”, “us” and “our” mean The Polished Version.
        </P>
        {identity.length > 0 && <P>{identity.join(" ")}</P>}
        <P>
          You can contact us about anything in this policy at <ContactEmail />.
        </P>
      </LegalSection>

      <LegalSection id="our-two-roles" heading="Our two roles">
        <LegalSubheading>Where we are the controller</LegalSubheading>
        <P>We decide how and why we use information about:</P>
        <List
          items={[
            "business owners who sign up for The Polished Version, and the team members (cleaners, supervisors and staff) they add;",
            "people who contact us, for example by email; and",
            "visitors to our own website, thepolishedversion.com.",
          ]}
        />
        <LegalSubheading>Where we are a processor</LegalSubheading>
        <P>
          Each cleaning business that uses The Polished Version has its own website (on a thepolishedversion.com web address or
          its own domain). When you book a clean, ask for a quote, request a callback, send a message or leave a review on one of
          those websites, <strong className="font-semibold text-foreground">that business is the controller</strong> of your
          information. We hold and process it only on the business’s instructions, under a data processing agreement that forms
          part of our <A href="/terms">Terms and Conditions</A>.
        </P>
        <P>
          If you are a customer of a cleaning business, please contact the business directly about your information. Each
          business’s website has its own privacy notice at /privacy. If you contact us instead, we will pass your request to the
          business and help it respond.
        </P>
      </LegalSection>

      <LegalSection id="what-we-collect" heading="Information we collect">
        <P>When we are the controller, we collect:</P>
        <List
          items={[
            <>
              <strong className="font-semibold text-foreground">Account details</strong>: your name, email address, password
              (stored only in scrambled, “hashed” form, so we can’t read it), phone number and profile photo if you add them, and
              your role in the business.
            </>,
            <>
              <strong className="font-semibold text-foreground">Business details</strong>: business name, logo, contact details,
              service areas, services and prices, opening hours, website content and images, booking and payment settings, your
              connected Stripe account ID or PayPal.me link, your Google Business Profile place ID if you choose to show Google
              reviews, and any custom domain you connect.
            </>,
            <>
              <strong className="font-semibold text-foreground">Team details</strong>: the names, email addresses, phone numbers,
              availability, schedules and job assignments of team members you add, messages between team members, and one-time
              invitation codes used to join your team.
            </>,
            <>
              <strong className="font-semibold text-foreground">Subscription details</strong>: your plan, trial dates and billing
              status, and the customer and subscription IDs Stripe gives us. Your card details are entered on Stripe’s secure pages
              and never reach us.
            </>,
            <>
              <strong className="font-semibold text-foreground">Messages to us</strong>: anything you send us by email, such as
              questions or support requests.
            </>,
            <>
              <strong className="font-semibold text-foreground">Activity and security records</strong>: an audit log of important
              actions in the admin (for example changing settings, deleting records, erasing a customer’s data or our staff
              opening an account for support), including who did it, when, and the IP address and browser used. We also keep a
              log of the account emails we send you, so that each one is sent only once.
            </>,
            <>
              <strong className="font-semibold text-foreground">Technical information</strong>: like most websites, our hosting
              and database providers record IP addresses, browser type and the pages or services requested, to deliver the site,
              keep it secure and fix faults.
            </>,
          ]}
        />
        <P>
          We don’t use analytics, advertising or tracking tools on our website or on businesses’ websites. Our{" "}
          <A href="/cookies">Cookie Policy</A> lists everything we store in your browser.
        </P>
      </LegalSection>

      <LegalSection id="how-we-use-it" heading="How we use it and our lawful basis">
        <P>UK data protection law requires us to have a lawful basis for each use of your information. Ours are:</P>
        <LegalTable
          caption="How we use personal information and the lawful basis for each use"
          head={["What we use it for", "Lawful basis"]}
          rows={[
            [
              "Creating and running your account, your business website and admin, and providing the features you use",
              "Contract — we need it to provide the service you signed up for",
            ],
            [
              "Running your free trial and subscription, taking payment through Stripe and handling billing questions",
              "Contract, and legal obligation for keeping accounting records",
            ],
            [
              "Sending account emails, such as welcome, trial ending, trial ended, payment failed, password reset, sign-up confirmation and team invitations",
              "Contract, and legitimate interests in keeping you informed about your account",
            ],
            [
              "Providing support, including opening your account in a read-only support view when you ask us to or when we need to fix a problem",
              "Contract, and legitimate interests in solving problems quickly",
            ],
            [
              "Keeping the platform and everyone’s data secure, preventing fraud and misuse, limiting repeated requests and keeping audit logs",
              "Legitimate interests in protecting our service, our customers and their customers",
            ],
            [
              "Understanding how the service is used through overall figures (for example the number of active businesses or bookings) so we can improve it",
              "Legitimate interests in running and improving our business",
            ],
            [
              "Meeting legal and regulatory duties, responding to lawful requests and dealing with legal claims",
              "Legal obligation, and legitimate interests in defending our legal rights",
            ],
          ]}
        />
        <P>
          Where we rely on legitimate interests, we have balanced them against your rights and you can object (see{" "}
          <A href="#your-rights">Your rights</A>). We only send emails about your account and the service. We don’t send
          marketing emails; if we ever do, we will give you a simple way to opt out and ask for your consent where the law
          requires it.
        </P>
        <P>We don’t make decisions about you based solely on automated processing that have legal or similarly significant effects.</P>
      </LegalSection>

      <LegalSection id="who-we-share-with" heading="Who we share it with">
        <P>
          We use the following service providers (“sub-processors”) to run The Polished Version. They may only use personal
          information to provide their service to us, under written contracts. This list applies both to information we control
          and to the information we process for cleaning businesses.
        </P>
        <LegalTable
          caption="Our service providers"
          head={["Provider", "What they do for us", "Where", "Safeguard for transfers"]}
          rows={[
            [
              "Supabase",
              "Database, sign-in, file storage (logos, website images, job photos) and server functions",
              "Data stored in the EU (Ireland). Supabase, Inc. is based in the US",
              "Data processing agreement with the EU Standard Contractual Clauses and the UK Addendum for any access from outside the UK and EEA",
            ],
            [
              "Vercel",
              "Website hosting and content delivery; connecting businesses’ custom domains",
              "Global network; Vercel Inc. is based in the US",
              "Data processing agreement with the EU Standard Contractual Clauses and the UK Addendum, or the UK–US data bridge where certified",
            ],
            [
              "Resend",
              "Sending emails, such as booking confirmations, reminders, invoices and account emails",
              "US",
              "Data processing agreement with the EU Standard Contractual Clauses and the UK Addendum, or the UK–US data bridge where certified",
            ],
            [
              "Stripe",
              "Subscription billing for The Polished Version, and card payments for businesses that connect their own Stripe account",
              "UK, EU and US",
              "Stripe’s data processing agreement and transfer safeguards. Stripe also acts as an independent controller for some payment information under its own privacy policy",
            ],
            [
              "Google",
              "Address and business search (Google Places), showing a business’s Google reviews, maps on businesses’ contact pages, website fonts, and the AI proposal writer and logo maker (Gemini API), which receive the job notes, customer name and business details a business enters",
              "Global; Google LLC is based in the US",
              "Google’s data processing terms with the EU Standard Contractual Clauses and the UK Addendum, or the UK–US data bridge where certified",
            ],
          ]}
        />
        <P>A few more things to know:</P>
        <List
          items={[
            "PayPal: if a business adds a PayPal.me link, we only show that link. We don’t send any information to PayPal; if you follow the link, PayPal’s own privacy policy applies.",
            "Custom domains: when a business connects its own domain, we register the domain name with Vercel and check its DNS settings using a public DNS service (Cloudflare). Only the domain name is involved.",
            "Google fonts and maps load directly in your browser, so Google receives your IP address. Address searches are sent to Google from our servers, not your browser.",
            "We may share information with professional advisers (such as lawyers and accountants), with authorities when the law requires it, or with a buyer if our business is sold or reorganised. In that case the buyer would have to respect this policy.",
          ]}
        />
        <P>We don’t sell personal information or share it for advertising.</P>
      </LegalSection>

      <LegalSection id="international-transfers" heading="International transfers">
        <P>
          Our main database is hosted by Supabase in the EU (Ireland). The UK recognises the EU as providing adequate protection
          for personal data. Some of our providers are based in, or access data from, the US or other countries. Where personal
          information leaves the UK or EEA, we rely on the safeguards in the table above: the UK International Data Transfer
          Addendum to the EU Standard Contractual Clauses, the UK International Data Transfer Agreement, or the UK–US data bridge
          for providers certified under it. You can ask us for more detail at <ContactEmail />.
        </P>
      </LegalSection>

      <LegalSection id="how-long" heading="How long we keep it">
        <List
          items={[
            <>
              <strong className="font-semibold text-foreground">Your business and its data</strong> (including your customers,
              bookings, team, invoices and uploaded images and job photos) are kept while your business account exists, including
              after a trial ends, so you can pick up where you left off. If the owner deletes the business from{" "}
              <strong className="font-semibold text-foreground">Settings → Your data</strong>, the website goes offline straight
              away, any subscription is cancelled, and all of the business’s data and files are permanently deleted 30 days later.
            </>,
            <>
              <strong className="font-semibold text-foreground">Your login</strong> (name and email) isn’t removed automatically
              when a business is deleted, because one login can belong to more than one business. Email us and we will delete it.
            </>,
            <>
              <strong className="font-semibold text-foreground">Subscription invoices</strong> are kept in our Stripe account for
              as long as the law requires us to keep accounting records (generally six years).
            </>,
            <>
              <strong className="font-semibold text-foreground">Audit logs</strong> are kept for at least 24 months for security
              and to show what happened if there is a problem or dispute. They are kept after a business is deleted, but contain
              only the activity record described above.
            </>,
            <>
              <strong className="font-semibold text-foreground">Team invitation codes</strong> expire shortly after they are sent
              and can only be used once.
            </>,
            <>
              <strong className="font-semibold text-foreground">Emails you send us</strong> are kept for as long as we need to deal
              with your message and any follow-up.
            </>,
            <>
              <strong className="font-semibold text-foreground">Server logs</strong> held by our hosting and database providers
              are kept for a short period, then deleted automatically.
            </>,
          ]}
        />
        <P>Copies in our providers’ routine backups are overwritten on their normal backup cycle.</P>
      </LegalSection>

      <LegalSection id="security" heading="How we keep it safe">
        <P>We use appropriate technical and organisational measures to protect personal information, including:</P>
        <List
          items={[
            "encryption in transit (HTTPS) for everything sent to and from our websites and services;",
            "database rules that keep each business’s data separate, so one business can never see another’s;",
            "role-based access, so team members only see what their role allows, and private storage for job photos;",
            "limits on repeated requests to protect sign-in, booking and contact forms from abuse;",
            "a tamper-resistant audit log of sensitive actions; and",
            "support access by our staff that is read-only, time-limited, needs a recorded reason and is logged.",
          ]}
        />
        <P>
          No system is completely secure. If a personal data breach happens that is likely to put your rights at risk, we will
          tell you, and the Information Commissioner’s Office where required, without undue delay.
        </P>
      </LegalSection>

      <LegalSection id="your-rights" heading="Your rights">
        <P>Under UK data protection law you have the right to:</P>
        <List
          items={[
            "access the personal information we hold about you and get a copy;",
            "have inaccurate information corrected (rectification);",
            "have your information deleted (erasure);",
            "ask us to limit how we use it (restriction);",
            "receive your information in a portable format, or have it sent to another organisation (portability);",
            "object to our use of it where we rely on legitimate interests; and",
            "withdraw consent at any time, where we rely on consent.",
          ]}
        />
        <P>
          To use any of these rights, email <ContactEmail />. It’s free, and we will reply within one month (we may extend this
          by up to two more months for complex requests and will tell you if so). We may need to confirm your identity first.
          Some rights have limits; for example, we may need to keep records the law requires.
        </P>
        <LegalSubheading>Self-service tools for business owners</LegalSubheading>
        <P>
          On every plan, owners can download their customers, bookings, enquiries, invoices and services as spreadsheet (CSV)
          files from <strong className="font-semibold text-foreground">Settings → Your data</strong>, and can delete their business
          from the same page. Owners can also erase an individual customer’s personal details: their enquiries, callback requests,
          messages and customer record are deleted, and their name and contact details are removed from bookings and invoices.
          Invoice amounts are kept as financial records.
        </P>
        <LegalSubheading>If you’re a customer of a cleaning business</LegalSubheading>
        <P>
          Please make your request to the business, which is the controller of your information. It can export or erase your
          details using the tools above. If you contact us, we will pass your request on.
        </P>
      </LegalSection>

      <LegalSection id="complaints" heading="Complaints">
        <P>
          If you’re unhappy with how we’ve handled your information, please contact us first at <ContactEmail /> so we can try
          to put it right. You also have the right to complain to the UK regulator, the Information Commissioner’s Office (ICO),
          at <A href="https://ico.org.uk/make-a-complaint/">ico.org.uk</A> or on 0303 123 1113.
        </P>
      </LegalSection>

      <LegalSection id="children" heading="Children">
        <P>
          The Polished Version is a service for businesses and isn’t intended for children. You must be 18 or over to create a
          business account. We don’t knowingly collect information about children for our own purposes.
        </P>
      </LegalSection>

      <LegalSection id="changes" heading="Changes to this policy">
        <P>
          We may update this policy when our service or the law changes. We will change the “Last updated” date above, and if
          the changes are significant we will tell business account holders by email or in the admin before they take effect.
        </P>
      </LegalSection>

      <LegalSection id="contact" heading="Contact us">
        <P>
          Email <ContactEmail /> with any questions about this policy or your information. Our website is{" "}
          <A href={LEGAL_ENTITY.websiteUrl}>thepolishedversion.com</A>.
        </P>
        {identity.length > 0 && <P>{identity.join(" ")}</P>}
      </LegalSection>
    </LegalDocument>
  );
}
